import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { 
  User, Mail, Phone, Shield, CheckCircle, XCircle, 
  Calendar, Clock, MapPin, Eye, FileCheck, Settings
} from 'lucide-react';
import { mapFarms } from '../../utils/mapMockData';
import { resolveIntlLocale } from '../../utils/locale';
import './ViewUserModal.css';

const getInitials = (firstName, lastName) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const getAvatarColor = (userId) => {
  // Use primary color to match layout
  return '#4A5A3A';
};

const getRoleBadgeConfig = (role) => {
  const configs = {
    superadmin: { icon: Shield, color: '#8A4A3A', bg: 'rgba(138, 74, 58, 0.12)', border: 'rgba(138, 74, 58, 0.2)' },
    admin: { icon: Shield, color: '#3A4A2A', bg: 'rgba(74, 90, 58, 0.12)', border: 'rgba(74, 90, 58, 0.2)' },
    reviewer: { icon: FileCheck, color: '#6A5A3A', bg: 'rgba(138, 122, 74, 0.12)', border: 'rgba(138, 122, 74, 0.2)' },
    viewer: { icon: Eye, color: '#4A4A4A', bg: 'rgba(107, 107, 107, 0.12)', border: 'rgba(107, 107, 107, 0.2)' },
    custom: { icon: Shield, color: '#7C3AED', bg: 'rgba(124, 58, 237, 0.12)', border: 'rgba(124, 58, 237, 0.2)' },
  };
  return configs[role] || configs.viewer;
};

export function ViewUserModal({ isOpen, onClose, user }) {
  const { t, i18n } = useTranslation();

  if (!user) return null;

  const locale = resolveIntlLocale(i18n.resolvedLanguage || i18n.language);

  const formatDate = (date) => {
    if (!date) return t('common.notAvailable');
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return t('common.notAvailable');
    return new Date(date).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPageLabel = (pageId) => t(`pages.${pageId}.title`, { defaultValue: pageId });

  const roleConfig = getRoleBadgeConfig(user.role);
  const RoleIcon = roleConfig.icon;

  // Get farm names
  const userFarms = user.farms
    ? mapFarms.filter(farm => user.farms.includes(farm.id)).map(farm => farm.name)
    : [];

  // Available pages for custom role
  const availablePages = [
    { id: 'dashboard', label: getPageLabel('dashboard'), icon: Settings },
    { id: 'analytics', label: getPageLabel('analytics'), icon: Settings },
    { id: 'map', label: getPageLabel('map'), icon: MapPin },
    { id: 'stations', label: getPageLabel('stations'), icon: Settings },
    { id: 'reports', label: getPageLabel('reports'), icon: FileCheck },
    { id: 'settings', label: getPageLabel('settings'), icon: Settings },
    { id: 'users', label: getPageLabel('users'), icon: User },
  ];

  const footer = (
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
      <Button
        type="button"
        variant="secondary"
        onClick={onClose}
      >
        {t('common.close')}
      </Button>
    </div>
  );

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="md"
      title={t('users.modals.viewUser.title')}
      footer={footer}
    >
      <div className="view-user-modal">
        {/* User Header */}
        <div className="view-user-header">
          <div
            className="view-user-avatar"
            style={{ background: getAvatarColor(user.id) }}
          >
            {getInitials(user.firstName, user.lastName)}
          </div>
          <div className="view-user-header-info">
            <h3 className="view-user-name">
              {user.firstName} {user.lastName}
            </h3>
            <p className="view-user-email">{user.email}</p>
          </div>
        </div>

        {/* User Information Sections */}
        <div className="view-user-content">
          {/* Basic Information */}
          <div className="view-user-section">
            <h4 className="view-user-section-title">{t('users.modals.viewUser.sections.basicInformation')}</h4>
            <div className="view-user-info-grid">
              <div className="view-user-info-item">
                <div className="view-user-info-icon">
                  <User size={16} />
                </div>
                <div className="view-user-info-content">
                  <div className="view-user-info-label">{t('users.modals.viewUser.fields.fullName')}</div>
                  <div className="view-user-info-value">
                    {user.firstName} {user.lastName}
                  </div>
                </div>
              </div>

              <div className="view-user-info-item">
                <div className="view-user-info-icon">
                  <Mail size={16} />
                </div>
                <div className="view-user-info-content">
                  <div className="view-user-info-label">{t('users.modals.addUser.fields.email.label')}</div>
                  <div className="view-user-info-value">{user.email}</div>
                </div>
              </div>

              {user.phone && (
                <div className="view-user-info-item">
                  <div className="view-user-info-icon">
                    <Phone size={16} />
                  </div>
                  <div className="view-user-info-content">
                    <div className="view-user-info-label">{t('users.modals.addUser.fields.phone.label')}</div>
                    <div className="view-user-info-value">{user.phone}</div>
                  </div>
                </div>
              )}

              <div className="view-user-info-item">
                <div className="view-user-info-icon">
                  <RoleIcon size={16} />
                </div>
                <div className="view-user-info-content">
                  <div className="view-user-info-label">{t('users.modals.addUser.fields.role.label')}</div>
                  <div className="view-user-info-value">
                    <span
                      className="view-role-badge"
                      style={{
                        background: roleConfig.bg,
                        color: roleConfig.color,
                        border: `1px solid ${roleConfig.border}`,
                      }}
                    >
                      <RoleIcon size={12} />
                      {t(`users.table.roles.${user.role}`, { defaultValue: user.role })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="view-user-info-item">
                <div className="view-user-info-icon">
                  {user.isActive ? <CheckCircle size={16} /> : <XCircle size={16} />}
                </div>
                <div className="view-user-info-content">
                  <div className="view-user-info-label">{t('users.modals.addUser.fields.status.label')}</div>
                  <div className="view-user-info-value">
                    {user.isActive ? (
                      <span className="view-status-badge view-status-badge--active">
                        <CheckCircle size={12} />
                        {t('users.table.status.active')}
                      </span>
                    ) : (
                      <span className="view-status-badge view-status-badge--inactive">
                        <XCircle size={12} />
                        {t('users.table.status.inactive')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Farm Access */}
          <div className="view-user-section">
            <h4 className="view-user-section-title">{t('users.modals.viewUser.sections.farmAccess')}</h4>
            <div className="view-user-farms">
              {userFarms.length > 0 ? (
                <div className="view-farms-list">
                  {userFarms.map((farmName, index) => (
                    <span key={index} className="view-farm-badge">
                      {farmName}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="view-user-empty">{t('users.modals.viewUser.empty.noFarmsAssigned')}</p>
              )}
            </div>
          </div>

          {/* Custom Permissions (if custom role) */}
          {user.role === 'custom' && user.customPermissions && (
            <div className="view-user-section">
              <h4 className="view-user-section-title">{t('users.modals.viewUser.sections.customPermissions')}</h4>
              
              {/* Page Access */}
              {user.customPermissions.pages && user.customPermissions.pages.length > 0 && (
                <div className="view-permissions-group">
                  <div className="view-permissions-label">{t('users.modals.viewUser.fields.pageAccess')}</div>
                  <div className="view-pages-list">
                    {user.customPermissions.pages.map((pageId) => {
                      const page = availablePages.find(p => p.id === pageId);
                      return (
                        <span key={pageId} className="view-page-badge">
                          {page ? page.label : getPageLabel(pageId)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Analytics Permissions */}
              {user.customPermissions.pages?.includes('analytics') && user.customPermissions.analyticsPermissions && (
                <div className="view-permissions-group">
                  <div className="view-permissions-label">{t('users.modals.viewUser.fields.analyticsPermissions')}</div>
                  <div className="view-permissions-list">
                    {user.customPermissions.analyticsPermissions.downloadData && (
                      <span className="view-permission-badge">
                        <CheckCircle size={12} />
                        {t('users.modals.viewUser.permissions.downloadData')}
                      </span>
                    )}
                    {!user.customPermissions.analyticsPermissions.downloadData && (
                      <span className="view-permission-badge view-permission-badge--disabled">
                        <XCircle size={12} />
                        {t('users.modals.viewUser.permissions.downloadDataDisabled')}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Reports Permissions */}
              {user.customPermissions.pages?.includes('reports') && user.customPermissions.reportsPermissions && (
                <div className="view-permissions-group">
                  <div className="view-permissions-label">{t('users.modals.viewUser.fields.reportsPermissions')}</div>
                  <div className="view-permissions-list">
                    {user.customPermissions.reportsPermissions.viewReports ? (
                      <span className="view-permission-badge">
                        <CheckCircle size={12} />
                        {t('users.modals.viewUser.permissions.viewReports')}
                      </span>
                    ) : (
                      <span className="view-permission-badge view-permission-badge--disabled">
                        <XCircle size={12} />
                        {t('users.modals.viewUser.permissions.viewReportsDisabled')}
                      </span>
                    )}
                    {user.customPermissions.reportsPermissions.downloadDocuments ? (
                      <span className="view-permission-badge">
                        <CheckCircle size={12} />
                        {t('users.modals.viewUser.permissions.downloadDocuments')}
                      </span>
                    ) : (
                      <span className="view-permission-badge view-permission-badge--disabled">
                        <XCircle size={12} />
                        {t('users.modals.viewUser.permissions.downloadDocumentsDisabled')}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Account Information */}
          <div className="view-user-section">
            <h4 className="view-user-section-title">{t('users.modals.viewUser.sections.accountInformation')}</h4>
            <div className="view-user-info-grid">
              {user.createdAt && (
                <div className="view-user-info-item">
                  <div className="view-user-info-icon">
                    <Calendar size={16} />
                  </div>
                  <div className="view-user-info-content">
                    <div className="view-user-info-label">{t('users.modals.viewUser.fields.createdAt')}</div>
                    <div className="view-user-info-value">{formatDate(user.createdAt)}</div>
                  </div>
                </div>
              )}

              {user.lastLogin && (
                <div className="view-user-info-item">
                  <div className="view-user-info-icon">
                    <Clock size={16} />
                  </div>
                  <div className="view-user-info-content">
                    <div className="view-user-info-label">{t('users.modals.viewUser.fields.lastLogin')}</div>
                    <div className="view-user-info-value">{formatDateTime(user.lastLogin)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
